import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { ScrollArea } from "./components/ui/scroll-area";
import { Progress } from "./components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "./components/ui/radio-group";
import { Label } from "./components/ui/label";
import {
  Brain,
  ChevronDown,
  Clock,
  Users,
  Target,
  Zap,
  MessageCircle,
  Send,
  X,
  CheckCircle,
  ArrowRight,
  Sparkles,
  BarChart3,
  Shield,
  Globe,
  Award,
  Building2,
  Loader2,
  Menu,
  BookOpen,
  GraduationCap,
  Lock,
  LogOut,
  User,
  ChevronLeft,
  ChevronRight,
  FileText,
  Trophy,
  Check,
  AlertCircle,
  Download,
  Tag,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  const refreshUser = async () => {
    if (token) await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requirePayment = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (requirePayment && !user.has_paid) return <Navigate to="/checkout" replace />;
  return children;
};

// Loading Screen
const LoadingScreen = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
  </div>
);

// Auth Pages
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[#0a0a0a] border-[#1a1a1a]" data-testid="login-card">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
              <span className="text-white font-bold text-xl">L</span>
            </div>
            <span className="text-xl font-semibold text-white">Levuka Venture Labs</span>
          </div>
          <CardTitle className="text-2xl gradient-text">Welcome Back</CardTitle>
          <CardDescription className="text-[#8a8a8a]">Sign in to continue your learning journey</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}
            <div>
              <Label className="text-[#8a8a8a]">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#050505] border-[#1a1a1a] text-white mt-1"
                placeholder="you@company.com"
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <Label className="text-[#8a8a8a]">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#050505] border-[#1a1a1a] text-white mt-1"
                placeholder="••••••••"
                required
                data-testid="login-password"
              />
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sign In
            </Button>
          </form>
          <p className="text-center mt-4 text-[#8a8a8a] text-sm">
            Don't have an account?{" "}
            <button onClick={() => navigate("/register")} className="text-blue-400 hover:underline">
              Sign up
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(name, email, password);
      navigate("/checkout");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[#0a0a0a] border-[#1a1a1a]" data-testid="register-card">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
              <span className="text-white font-bold text-xl">L</span>
            </div>
            <span className="text-xl font-semibold text-white">Levuka Venture Labs</span>
          </div>
          <CardTitle className="text-2xl gradient-text">Create Account</CardTitle>
          <CardDescription className="text-[#8a8a8a]">Start your AI leadership journey</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}
            <div>
              <Label className="text-[#8a8a8a]">Full Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-[#050505] border-[#1a1a1a] text-white mt-1"
                placeholder="John Smith"
                required
                data-testid="register-name"
              />
            </div>
            <div>
              <Label className="text-[#8a8a8a]">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#050505] border-[#1a1a1a] text-white mt-1"
                placeholder="you@company.com"
                required
                data-testid="register-email"
              />
            </div>
            <div>
              <Label className="text-[#8a8a8a]">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#050505] border-[#1a1a1a] text-white mt-1"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="register-password"
              />
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="register-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Account
            </Button>
          </form>
          <p className="text-center mt-4 text-[#8a8a8a] text-sm">
            Already have an account?{" "}
            <button onClick={() => navigate("/login")} className="text-blue-400 hover:underline">
              Sign in
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Checkout Page
const CheckoutPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState("");

  const basePrice = 2995;
  // Use final_price from API response if available (handles fixed price promos like VIP199)
  const finalPrice = promoApplied ? promoApplied.final_price : basePrice;

  useEffect(() => {
    if (user?.has_paid) navigate("/dashboard");
  }, [user, navigate]);

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setValidating(true);
    setError("");
    try {
      const response = await axios.post(`${API}/promo/validate?code=${promoCode}`);
      setPromoApplied(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid promo code");
      setPromoApplied(null);
    } finally {
      setValidating(false);
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        origin_url: window.location.origin,
        promo_code: promoApplied ? promoCode : null
      });
      
      // Handle free enrollment (100% discount)
      if (response.data.free_enrollment) {
        await refreshUser();
        navigate("/dashboard");
        return;
      }
      
      window.location.href = response.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold gradient-text mb-2">Complete Your Enrollment</h1>
          <p className="text-[#8a8a8a]">Get instant access to the Executive AI & ML Program</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <Card className="bg-[#0a0a0a] border-[#1a1a1a]" data-testid="order-summary">
            <CardHeader>
              <CardTitle className="text-white">Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-white font-semibold">Executive AI & ML Program</h3>
                  <p className="text-sm text-[#8a8a8a]">12-week self-paced course</p>
                </div>
                <span className="text-white">${basePrice.toLocaleString()}</span>
              </div>

              <Separator className="bg-[#1a1a1a]" />

              {/* Promo Code */}
              <div>
                <Label className="text-[#8a8a8a] text-sm">Promo Code</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    className="bg-[#050505] border-[#1a1a1a] text-white"
                    placeholder="Enter code"
                    data-testid="promo-input"
                  />
                  <Button
                    variant="outline"
                    onClick={validatePromo}
                    disabled={validating || !promoCode.trim()}
                    className="border-[#1a1a1a]"
                    data-testid="apply-promo"
                  >
                    {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                  </Button>
                </div>
                {promoApplied && (
                  <div className="mt-2 p-2 rounded bg-green-500/10 border border-green-500/20 text-green-400 text-sm flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    {promoApplied.discount_percent}% discount applied!
                  </div>
                )}
              </div>

              <Separator className="bg-[#1a1a1a]" />

              {promoApplied && (
                <div className="flex justify-between text-[#8a8a8a]">
                  <span>Discount ({promoApplied.discount_percent}%)</span>
                  <span className="text-green-400">-${(basePrice - finalPrice).toLocaleString()}</span>
                </div>
              )}

              <div className="flex justify-between text-lg font-bold">
                <span className="text-white">Total</span>
                <span className="text-white">${finalPrice.toLocaleString()}</span>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <Button
                className="w-full btn-primary py-6"
                onClick={handleCheckout}
                disabled={loading}
                data-testid="checkout-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    Proceed to Payment
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-[#8a8a8a]">
                Secure payment powered by Stripe
              </p>
            </CardContent>
          </Card>

          {/* What's Included */}
          <Card className="bg-[#0a0a0a] border-[#1a1a1a]">
            <CardHeader>
              <CardTitle className="text-white">What's Included</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4">
                {[
                  { icon: BookOpen, text: "6 comprehensive modules with in-depth written lessons" },
                  { icon: FileText, text: "Quizzes after each module to test your knowledge" },
                  { icon: Brain, text: "AI-powered course assistant for questions" },
                  { icon: Clock, text: "Self-paced learning - complete on your schedule" },
                  { icon: Award, text: "Certificate of completion upon passing" },
                  { icon: Users, text: "Access to executive community" },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="text-[#c0c0c0]">{item.text}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Payment Success Page
const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      navigate("/checkout");
      return;
    }
    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    if (attempts >= 10) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        setStatus("success");
        await refreshUser();
        setTimeout(() => navigate("/dashboard"), 3000);
      } else {
        setAttempts((a) => a + 1);
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      setAttempts((a) => a + 1);
      setTimeout(checkPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[#0a0a0a] border-[#1a1a1a] text-center p-8">
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Processing Payment</h2>
            <p className="text-[#8a8a8a]">Please wait while we confirm your payment...</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
            <p className="text-[#8a8a8a]">Welcome to the Executive AI & ML Program. Redirecting to your dashboard...</p>
          </>
        )}
        {status === "timeout" && (
          <>
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Payment Processing</h2>
            <p className="text-[#8a8a8a] mb-4">Your payment is being processed. Please check your email for confirmation.</p>
            <Button onClick={() => navigate("/dashboard")} className="btn-primary">
              Go to Dashboard
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [modulesRes, progressRes] = await Promise.all([
        axios.get(`${API}/course/modules`),
        axios.get(`${API}/course/progress`)
      ]);
      setModules(modulesRes.data.modules);
      setProgress(progressRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getModuleProgress = (moduleId) => {
    if (!progress?.modules) return null;
    return progress.modules.find((m) => m.module_id === moduleId);
  };

  const completedModules = progress?.modules?.filter((m) => m.completed).length || 0;
  const totalModules = modules.length;
  const overallProgress = totalModules > 0 ? (completedModules / totalModules) * 100 : 0;

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-[#1a1a1a] bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                <span className="text-white font-bold text-xl">L</span>
              </div>
              <div>
                <span className="font-semibold text-white">Levuka</span>
                <span className="text-[#8a8a8a] ml-1">Venture Labs</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[#8a8a8a] hidden sm:block">{user?.name}</span>
              <Button variant="ghost" size="sm" onClick={logout} className="text-[#8a8a8a]">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold gradient-text mb-2" data-testid="dashboard-welcome">
            Welcome back, {user?.name?.split(" ")[0]}!
          </h1>
          <p className="text-[#8a8a8a]">Continue your AI leadership journey</p>
        </div>

        {/* Progress Overview */}
        <Card className="bg-[#0a0a0a] border-[#1a1a1a] mb-8" data-testid="progress-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-white mb-1">Course Progress</h2>
                <p className="text-[#8a8a8a]">
                  {completedModules} of {totalModules} modules completed
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-48">
                  <Progress value={overallProgress} className="h-3" />
                </div>
                <span className="text-white font-bold">{Math.round(overallProgress)}%</span>
              </div>
            </div>
            {progress?.completed && progress?.overall_score >= 70 && (
              <div className="mt-4 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-3">
                  <Trophy className="w-6 h-6 text-green-400" />
                  <div>
                    <p className="text-green-400 font-semibold">Congratulations! You've completed the course!</p>
                    <p className="text-green-400/70 text-sm">Overall Score: {progress.overall_score}%</p>
                  </div>
                  <Button
                    size="sm"
                    className="ml-auto bg-green-500 hover:bg-green-600"
                    onClick={() => navigate("/certificate")}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Get Certificate
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Card 
            className="bg-[#0a0a0a] border-[#1a1a1a] cursor-pointer hover:border-blue-500/50 transition-all"
            onClick={() => navigate("/profile")}
            data-testid="profile-card"
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <User className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Learning Profile</h3>
                <p className="text-[#8a8a8a] text-sm">Personalize your AI tutor experience</p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#8a8a8a] ml-auto" />
            </CardContent>
          </Card>

          <Card 
            className="bg-[#0a0a0a] border-[#1a1a1a] cursor-pointer hover:border-purple-500/50 transition-all"
            onClick={() => navigate("/tools")}
            data-testid="tools-card"
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Practical AI Tools</h3>
                <p className="text-[#8a8a8a] text-sm">ROI calculator, AI assessor & case studies</p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#8a8a8a] ml-auto" />
            </CardContent>
          </Card>
        </div>

        {/* Modules Grid */}
        <h2 className="text-xl font-semibold text-white mb-4">Course Modules</h2>
        <div className="grid gap-4" data-testid="modules-grid">
          {modules.map((module, index) => {
            const moduleProgress = getModuleProgress(module.id);
            const isCompleted = moduleProgress?.completed;
            const quizScore = moduleProgress?.quiz_score;
            const isAccessible = index === 0 || getModuleProgress(modules[index - 1]?.id)?.completed;

            return (
              <Card
                key={module.id}
                className={`bg-[#0a0a0a] border-[#1a1a1a] transition-all ${
                  isAccessible ? "hover:border-blue-500/50 cursor-pointer" : "opacity-60"
                }`}
                onClick={() => isAccessible && navigate(`/module/${module.id}`)}
                data-testid={`module-card-${module.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isCompleted
                          ? "bg-green-500/20"
                          : isAccessible
                          ? "bg-blue-500/20"
                          : "bg-[#1a1a1a]"
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      ) : isAccessible ? (
                        <span className="text-blue-400 font-bold">{module.id}</span>
                      ) : (
                        <Lock className="w-5 h-5 text-[#8a8a8a]" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-1">{module.title}</h3>
                          <p className="text-[#8a8a8a] text-sm mb-2">{module.description}</p>
                          <div className="flex items-center gap-4 text-sm text-[#8a8a8a]">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {module.duration}
                            </span>
                            <span className="flex items-center gap-1">
                              <BookOpen className="w-4 h-4" />
                              {module.lesson_count} lessons
                            </span>
                          </div>
                        </div>
                        {quizScore !== undefined && quizScore !== null && (
                          <Badge
                            className={`${
                              quizScore >= 70 ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"
                            }`}
                          >
                            Quiz: {quizScore}%
                          </Badge>
                        )}
                      </div>
                    </div>
                    {isAccessible && <ChevronRight className="w-5 h-5 text-[#8a8a8a]" />}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>

      <Chatbot />
    </div>
  );
};

// Module Page
const ModulePage = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [moduleProgress, setModuleProgress] = useState(null);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeSpent, setTimeSpent] = useState(0);
  const [microQuizAnswer, setMicroQuizAnswer] = useState(null);
  const [microQuizResult, setMicroQuizResult] = useState(null);
  const [microQuizSubmitted, setMicroQuizSubmitted] = useState(false);

  useEffect(() => {
    fetchModule();
  }, [moduleId]);

  // Track time spent on lesson
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeSpent(t => t + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [currentLessonIndex]);

  // Reset micro quiz when changing lessons
  useEffect(() => {
    setMicroQuizAnswer(null);
    setMicroQuizResult(null);
    setMicroQuizSubmitted(false);
    setTimeSpent(0);
  }, [currentLessonIndex]);

  const fetchModule = async () => {
    try {
      const response = await axios.get(`${API}/course/module/${moduleId}`);
      setModule(response.data.module);
      setModuleProgress(response.data.progress);
    } catch (error) {
      console.error("Error fetching module:", error);
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const submitMicroQuiz = async () => {
    if (microQuizAnswer === null) return;
    try {
      const response = await axios.post(`${API}/course/micro-quiz/submit`, {
        lesson_id: currentLesson.id,
        selected_option: microQuizAnswer
      });
      setMicroQuizResult(response.data);
      setMicroQuizSubmitted(true);
    } catch (error) {
      console.error("Micro quiz error:", error);
    }
  };

  const goToNextLesson = async () => {
    // Complete current lesson with time tracking
    try {
      await axios.post(`${API}/course/lesson/complete`, {
        lesson_id: currentLesson.id,
        time_spent_seconds: timeSpent
      });
    } catch (error) {
      // Don't block navigation even if tracking fails
      console.error("Lesson tracking error:", error);
    }
    setCurrentLessonIndex((i) => Math.min(module.lessons.length - 1, i + 1));
  };

  if (loading) return <LoadingScreen />;
  if (!module) return null;

  const currentLesson = module.lessons[currentLessonIndex];
  const isLastLesson = currentLessonIndex === module.lessons.length - 1;
  const hasMicroQuiz = currentLesson.micro_quiz;
  const canProceed = !hasMicroQuiz || microQuizSubmitted;

  // Render lesson content with proper HTML structure
  const renderContent = (content) => {
    return content
      .replace(/<section>/g, '<div class="mb-8">')
      .replace(/<\/section>/g, '</div>')
      .replace(/<h2>(.*?)<\/h2>/g, '<h2 class="text-2xl font-bold text-white mb-4 mt-8">$1</h2>')
      .replace(/<h3>(.*?)<\/h3>/g, '<h3 class="text-xl font-semibold text-white mb-3 mt-6">$1</h3>')
      .replace(/<h4>(.*?)<\/h4>/g, '<h4 class="text-lg font-semibold text-blue-400 mb-2 mt-4">$1</h4>')
      .replace(/<p>(.*?)<\/p>/g, '<p class="text-[#c0c0c0] mb-4 leading-relaxed">$1</p>')
      .replace(/<strong>(.*?)<\/strong>/g, '<strong class="text-white font-semibold">$1</strong>')
      .replace(/<ul>/g, '<ul class="list-disc list-inside space-y-2 mb-4 text-[#c0c0c0]">')
      .replace(/<ol>/g, '<ol class="list-decimal list-inside space-y-2 mb-4 text-[#c0c0c0]">')
      .replace(/<li>(.*?)<\/li>/g, '<li class="ml-4">$1</li>')
      .replace(/class="key-insight"/g, 'class="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6"')
      .replace(/class="concept-card"/g, 'class="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4 mb-4"')
      .replace(/class="myth-buster"/g, 'class="grid md:grid-cols-2 gap-4 mb-6"')
      .replace(/class="myth"/g, 'class="bg-red-500/10 border border-red-500/30 rounded-lg p-4"')
      .replace(/class="reality"/g, 'class="bg-green-500/10 border border-green-500/30 rounded-lg p-4"')
      .replace(/class="real-example"/g, 'class="text-sm text-blue-400 mt-2"')
      .replace(/class="checklist"/g, 'class="space-y-4 mb-6"')
      .replace(/class="check-item"/g, 'class="flex items-start gap-4 bg-[#1a1a1a] p-4 rounded-lg"')
      .replace(/class="check"/g, 'class="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold flex-shrink-0"')
      .replace(/class="reflection"/g, 'class="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6"')
      .replace(/class="action-item"/g, 'class="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6"')
      .replace(/class="knowledge-check"/g, 'class="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-6"')
      .replace(/class="stat-highlight"/g, 'class="text-center bg-[#1a1a1a] rounded-lg p-6 mb-6"')
      .replace(/class="stat"/g, 'class="text-5xl font-bold text-blue-400 mb-2"');
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-[#1a1a1a] bg-[#0a0a0a] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <Separator orientation="vertical" className="h-6 bg-[#1a1a1a]" />
              <div>
                <h1 className="text-white font-semibold">Module {module.id}: {module.title}</h1>
                <p className="text-xs text-[#8a8a8a]">
                  Lesson {currentLessonIndex + 1} of {module.lessons.length} • {currentLesson.estimated_time || '5 min'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-[#8a8a8a]">{Math.floor(timeSpent / 60)}:{(timeSpent % 60).toString().padStart(2, '0')}</span>
              <Progress value={((currentLessonIndex + 1) / module.lessons.length) * 100} className="w-32 h-2" />
            </div>
          </div>
        </div>
      </header>

      {!showQuiz ? (
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Lesson Navigation */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {module.lessons.map((lesson, index) => (
              <Button
                key={lesson.id}
                variant={index === currentLessonIndex ? "default" : "outline"}
                size="sm"
                onClick={() => setCurrentLessonIndex(index)}
                className={`${index === currentLessonIndex ? "bg-blue-500" : "border-[#1a1a1a]"} flex-shrink-0`}
              >
                <span className="mr-2">{index + 1}</span>
                <span className="hidden sm:inline text-xs truncate max-w-[100px]">{lesson.title}</span>
              </Button>
            ))}
          </div>

          {/* Lesson Content */}
          <Card className="bg-[#0a0a0a] border-[#1a1a1a]" data-testid="lesson-content">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="border-blue-500/30 text-blue-400">
                  <Clock className="w-3 h-3 mr-1" />
                  {currentLesson.estimated_time || '5 min read'}
                </Badge>
              </div>
              <CardTitle className="text-2xl text-white">{currentLesson.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="lesson-content"
                dangerouslySetInnerHTML={{ __html: renderContent(currentLesson.content) }}
              />
            </CardContent>
          </Card>

          {/* Micro Quiz */}
          {hasMicroQuiz && (
            <Card className="bg-purple-500/5 border-purple-500/30 mt-6" data-testid="micro-quiz">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-400" />
                  Quick Check
                </CardTitle>
                <CardDescription className="text-[#8a8a8a]">
                  Answer this before moving on
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-white mb-4">{currentLesson.micro_quiz.question}</p>
                <RadioGroup
                  value={microQuizAnswer?.toString()}
                  onValueChange={(v) => setMicroQuizAnswer(parseInt(v))}
                  disabled={microQuizSubmitted}
                  className="space-y-3"
                >
                  {currentLesson.micro_quiz.options.map((option, index) => (
                    <div
                      key={index}
                      className={`flex items-center space-x-3 p-4 rounded-lg border transition-colors ${
                        microQuizSubmitted
                          ? index === currentLesson.micro_quiz.correct
                            ? "border-green-500 bg-green-500/10"
                            : microQuizAnswer === index
                            ? "border-red-500 bg-red-500/10"
                            : "border-[#1a1a1a]"
                          : microQuizAnswer === index
                          ? "border-purple-500 bg-purple-500/10"
                          : "border-[#1a1a1a] hover:border-[#2a2a2a]"
                      }`}
                    >
                      <RadioGroupItem value={index.toString()} id={`micro-${index}`} />
                      <Label htmlFor={`micro-${index}`} className="flex-1 cursor-pointer text-[#c0c0c0]">
                        {option}
                      </Label>
                      {microQuizSubmitted && index === currentLesson.micro_quiz.correct && (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      )}
                    </div>
                  ))}
                </RadioGroup>

                {!microQuizSubmitted ? (
                  <Button 
                    onClick={submitMicroQuiz}
                    disabled={microQuizAnswer === null}
                    className="mt-4 bg-purple-500 hover:bg-purple-600"
                  >
                    Check Answer
                  </Button>
                ) : (
                  <div className={`mt-4 p-4 rounded-lg ${microQuizResult?.correct ? 'bg-green-500/10 border border-green-500/30' : 'bg-blue-500/10 border border-blue-500/30'}`}>
                    <p className={`font-semibold ${microQuizResult?.correct ? 'text-green-400' : 'text-blue-400'}`}>
                      {microQuizResult?.correct ? '✓ Correct!' : 'Not quite - here\'s why:'}
                    </p>
                    <p className="text-[#c0c0c0] mt-2">{currentLesson.micro_quiz.explanation}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              onClick={() => setCurrentLessonIndex((i) => Math.max(0, i - 1))}
              disabled={currentLessonIndex === 0}
              className="border-[#1a1a1a]"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous Lesson
            </Button>
            {isLastLesson ? (
              <Button 
                className="btn-primary" 
                onClick={() => setShowQuiz(true)} 
                disabled={!canProceed}
                data-testid="take-quiz-btn"
              >
                Take Module Quiz
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                className="btn-primary"
                onClick={goToNextLesson}
                disabled={!canProceed}
              >
                Next Lesson
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>

          {!canProceed && (
            <p className="text-center text-[#8a8a8a] text-sm mt-4">
              Complete the Quick Check above to continue
            </p>
          )}
        </div>
      ) : (
        <QuizComponent module={module} onComplete={() => navigate("/dashboard")} />
      )}

      <Chatbot />
    </div>
  );
};

// Quiz Component
const QuizComponent = ({ module, onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const questions = module.quiz.questions;
  const question = questions[currentQuestion];

  const handleAnswer = (value) => {
    setAnswers({ ...answers, [question.id]: parseInt(value) });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const submission = {
        module_id: module.id,
        answers: Object.entries(answers).map(([question_id, selected_option]) => ({
          question_id,
          selected_option
        }))
      };
      const response = await axios.post(`${API}/course/quiz/submit`, submission);
      setResults(response.data);
      setSubmitted(true);
    } catch (error) {
      console.error("Quiz submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted && results) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card className="bg-[#0a0a0a] border-[#1a1a1a]" data-testid="quiz-results">
          <CardContent className="p-8 text-center">
            {results.passed ? (
              <>
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-white mb-2">Congratulations!</h2>
                <p className="text-[#8a8a8a] mb-4">You passed the quiz with a score of {results.score}%</p>
              </>
            ) : (
              <>
                <AlertCircle className="w-20 h-20 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-white mb-2">Keep Learning!</h2>
                <p className="text-[#8a8a8a] mb-4">
                  You scored {results.score}%. You need {module.quiz.passing_score}% to pass.
                </p>
              </>
            )}
            <div className="bg-[#1a1a1a] rounded-lg p-4 mb-6">
              <div className="text-4xl font-bold text-white mb-1">{results.score}%</div>
              <div className="text-[#8a8a8a]">
                {results.correct_count} of {results.total_questions} correct
              </div>
            </div>
            <Button className="btn-primary" onClick={onComplete}>
              {results.passed ? "Continue to Dashboard" : "Review and Try Again"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <Card className="bg-[#0a0a0a] border-[#1a1a1a]" data-testid="quiz-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Module {module.id} Quiz</CardTitle>
            <Badge variant="outline" className="border-[#1a1a1a]">
              Question {currentQuestion + 1} of {questions.length}
            </Badge>
          </div>
          <Progress value={((currentQuestion + 1) / questions.length) * 100} className="mt-4" />
        </CardHeader>
        <CardContent>
          <h3 className="text-lg text-white mb-6">{question.question}</h3>
          <RadioGroup
            value={answers[question.id]?.toString()}
            onValueChange={handleAnswer}
            className="space-y-3"
          >
            {question.options.map((option, index) => (
              <div
                key={index}
                className={`flex items-center space-x-3 p-4 rounded-lg border transition-colors ${
                  answers[question.id] === index
                    ? "border-blue-500 bg-blue-500/10"
                    : "border-[#1a1a1a] hover:border-[#2a2a2a]"
                }`}
              >
                <RadioGroupItem value={index.toString()} id={`option-${index}`} />
                <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer text-[#c0c0c0]">
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>

          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={() => setCurrentQuestion((q) => Math.max(0, q - 1))}
              disabled={currentQuestion === 0}
              className="border-[#1a1a1a]"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            {currentQuestion === questions.length - 1 ? (
              <Button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={Object.keys(answers).length !== questions.length || loading}
                data-testid="submit-quiz"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Quiz
              </Button>
            ) : (
              <Button
                className="btn-primary"
                onClick={() => setCurrentQuestion((q) => Math.min(questions.length - 1, q + 1))}
                disabled={answers[question.id] === undefined}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Certificate Page
const CertificatePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchCertificate();
  }, []);

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(`${API}/course/certificate`);
      setCertificate(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to generate certificate");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingScreen />;

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[#0a0a0a] border-[#1a1a1a] text-center p-8">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Certificate Not Available</h2>
          <p className="text-[#8a8a8a] mb-4">{error}</p>
          <Button onClick={() => navigate("/dashboard")} className="btn-primary">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-6">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Certificate */}
        <div
          className="bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a] border-4 border-blue-500/30 rounded-lg p-12 text-center"
          data-testid="certificate"
        >
          <div className="border-2 border-[#2a2a2a] rounded-lg p-8">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center">
                <span className="text-white font-bold text-2xl">L</span>
              </div>
              <span className="text-2xl font-semibold text-white">Levuka Venture Labs</span>
            </div>

            <h1 className="text-4xl font-bold gradient-text mb-4">Certificate of Completion</h1>
            <p className="text-[#8a8a8a] mb-8">This is to certify that</p>

            <h2 className="text-3xl font-bold text-white mb-8 border-b border-[#2a2a2a] pb-4 mx-auto max-w-md">
              {certificate.recipient_name}
            </h2>

            <p className="text-[#8a8a8a] mb-4">has successfully completed the</p>
            <h3 className="text-2xl font-semibold text-blue-400 mb-8">{certificate.course_name}</h3>

            <div className="flex justify-center gap-12 mb-8">
              <div>
                <p className="text-[#8a8a8a] text-sm">Score</p>
                <p className="text-white font-bold text-xl">{certificate.score}%</p>
              </div>
              <div>
                <p className="text-[#8a8a8a] text-sm">Date</p>
                <p className="text-white font-bold text-xl">
                  {new Date(certificate.completion_date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                  })}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 text-[#8a8a8a]">
              <Award className="w-5 h-5" />
              <span>Certificate ID: {certificate.certificate_id}</span>
            </div>
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-[#8a8a8a] text-sm">
            This certificate can be verified at levukalabs.com/verify
          </p>
        </div>
      </div>
    </div>
  );
};

// Chatbot Component
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I'm your AI Course Assistant. How can I help you with your learning today?"
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, { session_id: sessionId, message: userMessage });
      setMessages((prev) => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "I'm having trouble connecting. Please try again." }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full btn-primary flex items-center justify-center shadow-lg z-50 hover:scale-110 transition-transform"
        data-testid="chatbot-trigger"
      >
        <MessageCircle className="w-6 h-6 text-white" />
      </button>

      {isOpen && (
        <div className="fixed bottom-24 right-6 w-[380px] max-w-[calc(100vw-48px)] h-[500px] glass-card flex flex-col z-50 overflow-hidden shadow-2xl">
          <div className="p-4 border-b border-[#1a1a1a] flex items-center justify-between bg-[#0a0a0a]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Brain className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="font-semibold text-white">AI Course Assistant</h4>
                <p className="text-xs text-green-400 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-400" />
                  Online
                </p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/10 rounded-lg">
              <X className="w-5 h-5 text-[#8a8a8a]" />
            </button>
          </div>

          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div key={index} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl ${
                      message.role === "user"
                        ? "bg-blue-500 text-white rounded-br-md"
                        : "bg-[#1a1a1a] text-[#c0c0c0] rounded-bl-md"
                    }`}
                  >
                    {message.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-[#1a1a1a] p-3 rounded-2xl rounded-bl-md">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="p-4 border-t border-[#1a1a1a] bg-[#0a0a0a]">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Ask about the course..."
                className="bg-[#050505] border-[#1a1a1a] text-white"
                data-testid="chatbot-input"
              />
              <Button onClick={sendMessage} disabled={isLoading || !input.trim()} className="btn-primary px-4">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Landing Page (simplified for space)
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user?.has_paid) navigate("/dashboard");
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-[#050505]/90 backdrop-blur-lg border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                <span className="text-white font-bold text-xl">L</span>
              </div>
              <span className="font-semibold text-white">Levuka Venture Labs</span>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <Button className="btn-primary rounded-full" onClick={() => navigate("/dashboard")}>
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate("/login")} className="text-[#8a8a8a]">
                    Sign In
                  </Button>
                  <Button className="btn-primary rounded-full" onClick={() => navigate("/register")}>
                    Enroll Now
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <Badge variant="outline" className="mb-6 px-4 py-2 border-blue-500/30 text-blue-400">
            <Sparkles className="w-4 h-4 mr-2" />
            Self-Paced Executive Program
          </Badge>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold gradient-text leading-tight mb-6">
            Master AI & Machine Learning
            <br />
            <span className="accent-gradient">for Business Leaders</span>
          </h1>
          <p className="text-xl text-[#8a8a8a] max-w-3xl mx-auto mb-10">
            A 12-week self-paced program with written lessons, interactive quizzes, and a certificate of completion. 
            Learn at your own pace and deploy AI in your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              size="lg"
              className="btn-primary px-8 py-6 text-lg rounded-full"
              onClick={() => navigate("/register")}
            >
              Start Learning - $2,995
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { icon: BookOpen, value: "6", label: "Modules" },
              { icon: FileText, value: "18", label: "Lessons" },
              { icon: Brain, value: "30", label: "Quiz Questions" },
              { icon: Award, value: "100%", label: "Self-Paced" },
            ].map((stat, index) => (
              <div key={index} className="glass-card p-4 text-center">
                <stat.icon className="w-6 h-6 mx-auto mb-2 text-blue-400" />
                <div className="text-2xl font-bold text-white">{stat.value}</div>
                <div className="text-sm text-[#8a8a8a]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#050505] to-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold gradient-text text-center mb-12">What You'll Learn</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "AI Foundations", desc: "Core concepts for business leaders" },
              { title: "Data Strategy", desc: "Building data-driven organizations" },
              { title: "ML Applications", desc: "Predictive analytics & automation" },
              { title: "Deep Learning", desc: "Computer vision & NLP" },
              { title: "Generative AI", desc: "LLMs and prompt engineering" },
              { title: "AI Strategy", desc: "Implementation & ROI frameworks" },
            ].map((item, i) => (
              <Card key={i} className="bg-[#0a0a0a] border-[#1a1a1a]">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-[#8a8a8a]">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold gradient-text mb-4">Ready to Lead with AI?</h2>
          <p className="text-[#8a8a8a] mb-8">
            Join executives from Fortune 500 companies in mastering AI strategy.
          </p>
          <Button size="lg" className="btn-primary px-8 py-6 text-lg rounded-full" onClick={() => navigate("/register")}>
            Enroll Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-sm text-[#8a8a8a] mt-4">Use code LAUNCH25 for 25% off</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto text-center text-[#8a8a8a] text-sm">
          © 2025 Levuka Venture Labs. All rights reserved.
        </div>
      </footer>

      <Chatbot />
    </div>
  );
};

// useParams hook import
import { useParams } from "react-router-dom";

// Import pages
import ProfilePage from "./pages/ProfilePage";
import ToolsPage from "./pages/ToolsPage";

// Main App
function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route
              path="/checkout"
              element={
                <ProtectedRoute>
                  <CheckoutPage />
                </ProtectedRoute>
              }
            />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requirePayment>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/module/:moduleId"
              element={
                <ProtectedRoute requirePayment>
                  <ModulePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificate"
              element={
                <ProtectedRoute requirePayment>
                  <CertificatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute requirePayment>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools"
              element={
                <ProtectedRoute requirePayment>
                  <ToolsPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
